import React, { useState, useEffect } from 'react';
import { pick } from 'lodash';
import { CircularProgress, Grid, Box, TextField as MuiTextField, FormControl, Select as MuiSelect, Divider } from '@mui/material';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useHistory, useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import useUser from '../lib/useUser';
import { apiFetch } from '../lib/fetch';
import Layout from '../components/layout';
import { Button } from '../lib/common';
import { NumericFormat } from 'react-number-format';
import { formatCurrency } from '../lib/util';
import { colors } from '../lib/styles';
import IncomePlanWrapper from '../components/income-plan-wrapper';
import { toggleForm, generateRedesignedIncomePlan } from '../lib/client';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  ChartDataLabels,
);

// Custom NumericFormatCustom component for currency input
const NumericFormatCustom = React.forwardRef(function NumericFormatCustom(props, ref) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      valueIsNumericString
      prefix="$"
      decimalScale={2}
      fixedDecimalScale
    />
  );
});

// Custom NumericFormatInteger component for age inputs
const NumericFormatInteger = React.forwardRef(function NumericFormatInteger(props, ref) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      valueIsNumericString
      decimalScale={0}
      allowNegative={false}
      isAllowed={(values) => {
        const { value } = values;
        return (value >= 0 && value <= 120) || value === '';
      }}
    />
  );
});


// Compliance-locked assumption rates (Summit Compliance Group). These are
// non-editable per regulatory requirement; the UI displays them but the user
// cannot change them, and they are never read from the saved client record.
const LOCKED_EQUITY_GROWTH_RATE = 7.5;
const LOCKED_BOND_GROWTH_RATE = 2.2;
const LOCKED_SOR_AVG_RETURN = 6;

const styles = {
  mainBody: {
    padding: '20px',
  },
  errTxt: {
    color: 'red',
    margin: '5px',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '1.5em',
  },
}

const DefaultRetirementPlan = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const history = useHistory();
  const auth = useUser();
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [err, setErr] = useState(null);
  const [savingView, setSavingView] = useState(false);
  
  useEffect(() => {
    if (!auth?.id) return;
    apiFetch(`/users/${auth.id}/ip_households/${id}`)
    .then((res) => {
      return generateRedesignedIncomePlan(res);
    }).then((generatedClient) => {
      setClient(generatedClient);
    }).catch((e) => {
      let err = e.toString();
      setErr(err);
    });
  }, [auth?.id, id]);

  if (!client) {
    return <Layout><div>Loading...</div></Layout>;
  }

  const {
    leg1Compounded,
    leg2Compounded,
    leg3Compounded,
    displayName,
    totalRetAssets,
    age,
    spouseAge,
    emergencyFunds,
    planIncomePrincipal,
    growthPrincipal,
    leg1AnnualIncome,
    leg2AnnualIncome,
    leg3AnnualIncome,
    recoveryAssets,
    additionalNeedsPercentage,
    pieChartIncomePercentage,
    pieChartGrowthPercentage,
  } = client;

  // Calculate current investable assets based on NQ inclusion setting
  const totalNQAssets = client.assets?.filter((a) => a.type === 'NON_QUALIFIED')
    .reduce((acc, a) => acc + (Number(a.value) || 0), 0) || 0;
  
  const currentInvestableAssets = client.include_nq_assets !== false
    ? totalRetAssets
    : totalRetAssets - totalNQAssets;

  // --- Income sources at the client's retirement year ---
  // Each person's Social Security / Pension only counts if they have reached
  // their own start age by the time the client retires. This mirrors the
  // year-by-year projection in src/lib/client.js (onSS / spouseOnSS gating)
  // so the Default page no longer adds a spouse who hasn't started yet.
  const retirementAge = Number(client.retirement_age) || 0;
  const yearsToRetirement = retirementAge && age ? Math.max(0, retirementAge - Number(age)) : 2;
  const inflationFactor = Math.pow(1 + ((Number(client.inflation_rate) || 2.5) / 100), yearsToRetirement);

  // Client's own age at retirement is simply retirement_age. Spouse's age at
  // that same point is their current age plus the years until the client retires.
  const spouseAgeAtRetirement = spouseAge ? Number(spouseAge) + yearsToRetirement : null;

  const ssStartAge = Number(client.ss_age) || retirementAge;
  const spouseSsStartAge = Number(client.spouse_ss_age) || Number(client.spouse_retirement_age) || 0;
  const pensionStartAge = Number(client.pension_age) || retirementAge;
  const spousePensionStartAge = Number(client.spouse_pension_age) || Number(client.spouse_retirement_age) || 0;

  const clientOnSS = retirementAge >= ssStartAge;
  const spouseOnSS = spouseAgeAtRetirement != null && spouseAgeAtRetirement >= spouseSsStartAge;
  const clientOnPension = retirementAge >= pensionStartAge;
  const spouseOnPension = spouseAgeAtRetirement != null && spouseAgeAtRetirement >= spousePensionStartAge;

  const ssiIncome = (
    (clientOnSS ? (Number(client.social_security) || 0) : 0) +
    (spouseOnSS ? (Number(client.spouse_social_security) || 0) : 0)
  ) * inflationFactor;

  const pensionIncome = (
    (clientOnPension ? (Number(client.pension) || 0) : 0) +
    (spouseOnPension ? (Number(client.spouse_pension) || 0) : 0)
  ) * inflationFactor;

  // Rental income is not tied to a start age; both spouses' rental always applies.
  const rentalIncome = (
    (Number(client.rental) || 0) + (Number(client.spouse_rental) || 0)
  ) * inflationFactor;

  const assetWithdrawalIncome = (
    (currentInvestableAssets * ((Number(client.stock_allocation) || 60) / 100) * Math.pow(1 + (LOCKED_EQUITY_GROWTH_RATE / 100), yearsToRetirement)) +
    (currentInvestableAssets * ((Number(client.bond_allocation) || 40) / 100) * Math.pow(1 + (LOCKED_BOND_GROWTH_RATE / 100), yearsToRetirement))
  ) * ((Number(client.asset_withdrawal_rate) || 4) / 100);


  const handleTextChange = async (evt) => {
    const { name, value } = evt.target;
    const newClient = { ...client, [name]: value };
    const redesignedClient = await generateRedesignedIncomePlan(newClient);
    setClient(redesignedClient);
  };


  const handleMaskEditChange = async (evt) => {
    // Handle both legacy MaskInput format and new direct TextField format
    const { name, value } = evt.target ? evt.target : evt;
    const newClient = { ...client, [name]: value };
    const redesignedClient = await generateRedesignedIncomePlan(newClient);
    setClient(redesignedClient);
  }

  const handleSubmit = async () => {
    setErr(null);
    // const leg1Allocation = Number(client.leg1_allocation) || 65;
    // const leg2Allocation = Number(client.leg2_allocation) || 20;
    // const leg3Allocation = Number(client.leg3_allocation) || 15;
    const totalPrincipals = Number(client.leg1_principle) + Number(client.leg2_principle) + Number(client.leg3_principle);
    if (totalPrincipals > Number(planIncomePrincipal)) {
      setErr('Principal allocations must not total greater than income bucket');
      return;
    }
    const toSave = pick(client, [
      'firstname',
      'lastname',
      'dob',
      'leg1_compounded',
      'leg2_compounded',
      'leg3_compounded',
      'inflation_rate',
      'desired_recovery',
      'emergency_fund',
      'effective_tax_rate',
      'leg1_allocation',
      'leg2_allocation',
      'leg3_allocation',
      'leg1_income_bonus_rate',
      'leg2_income_bonus_rate',
      'leg3_income_bonus_rate',
      'leg1_rider_annual_increase',
      'leg2_rider_annual_increase',
      'leg3_rider_annual_increase',
      'leg1_withdrawal_rate',
      'leg2_withdrawal_rate',
      'leg3_withdrawal_rate',
      'leg1_age_to_begin',
      'leg2_age_to_begin',
      'leg3_age_to_begin',
      'plan_year',
      'income_bucket',
      'plan_names',
      'leg1_product',
      'leg2_product',
      'leg3_product',
      'leg1_years_deferred',
      'leg2_years_deferred',
      'leg3_years_deferred',
      'leg1_principle',
      'leg2_principle',
      'leg3_principle',
      'years_to_grow',
      'assumed_net_growth',
      // equity_growth_rate, bond_growth_rate, sor_risk_avg_ret are compliance-locked
      // (non-editable) and intentionally excluded so no stale value is persisted.
      'asset_withdrawal_rate',
      'include_nq_assets',
      'stock_allocation', 
      'bond_allocation',
      'leg1_rider_rate',
      'leg2_rider_rate',
      'leg3_rider_rate',
    ]);
    toSave.desired_recovery = toSave.desired_recovery || totalRetAssets;
    toSave.effective_tax_rate = Number(toSave.effective_tax_rate) || 0.0;
    toSave.emergency_fund = toSave.emergency_fund || 100000;
    console.log('saving', toSave, auth.id, id, 'ASSETS', client.assets);
    try {
      const saved = await apiFetch.put(`/users/${auth.id}/ip_households/${id}`, toSave);
      saved.assets = client.assets;
      const redesignedClient = await generateRedesignedIncomePlan(saved);
      setClient(redesignedClient);
      setOpen(true);
      setMessage('Saved');
      return redesignedClient;
    } catch (e) {
      let err = e.toString();
      setErr(err);
    }
    return null;
  };
  
  // Generate sequence of returns risk data with inflation-adjusted withdrawals
  const generateScenarioData = (startingBalance, baseWithdrawal, returns, inflationRate) => {
    const data = [];
    let balance = startingBalance;
    
    for (let year = 0; year <= 40; year++) {
      data.push(Math.max(0, balance));
      if (year < returns.length && balance > 0) {
        // Calculate inflation-adjusted withdrawal for this year
        const adjustedWithdrawal = baseWithdrawal * Math.pow(1 + inflationRate / 100, year);
        balance = balance * (1 + returns[year] / 100) - adjustedWithdrawal;
        balance = Math.max(0, balance); // Don't allow negative balance
      }
    }
    return data;
  };

  const startingBalance = currentInvestableAssets;
  const annualWithdrawal = startingBalance * ((Number(client.asset_withdrawal_rate) || 4) / 100);
  
  // Scenario 1: Good returns early (25%, 10%, 5%, -15% repeating)
  const scenario1Returns = [];
  const pattern1 = [25, 10, 5, -15];
  for (let i = 0; i < 40; i++) {
    scenario1Returns.push(pattern1[i % 4]);
  }
  
  // Scenario 2: Bad returns early (-15%, 5%, 10%, 25% repeating)  
  const scenario2Returns = [];
  const pattern2 = [-15, 5, 10, 25];
  for (let i = 0; i < 40; i++) {
    scenario2Returns.push(pattern2[i % 4]);
  }

  const inflationRate = Number(client.inflation_rate) || 2.5;

  const chartData = {
    labels: Array.from({ length: 41 }, (_, i) => i.toString()),
    datasets: [
      {
        label: 'Balance - Scenario 1',
        data: generateScenarioData(startingBalance, annualWithdrawal, scenario1Returns, inflationRate),
        borderColor: colors.primaryColor,
        backgroundColor: 'transparent',
        borderWidth: 3,
        tension: 0.1,
      },
      {
        label: 'Balance - Scenario 2', 
        data: generateScenarioData(startingBalance, annualWithdrawal, scenario2Returns, inflationRate),
        borderColor: colors.errorRed,
        backgroundColor: 'transparent',
        borderWidth: 3,
        tension: 0.1,
      },
    ],
  };

  const options = {
    responsive: false,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        display: false,
      },
      title: {
        display: true,
        text: 'Sequence of Returns Risk',
        font: {
          size: 18,
          weight: 'bold'
        },
        color: colors.darkAccent,
        padding: 20,
      },
      legend: {
        position: 'top',
        labels: {
          boxWidth: 15,
          padding: 15,
          usePointStyle: true,
          font: {
            size: 14,
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Year',
          font: {
            size: 14,
            weight: 'bold'
          }
        },
        grid: {
          color: '#e0e0e0',
        },
        ticks: {
          maxTicksLimit: 10,
        }
      },
      y: {
        title: {
          display: true,
          text: 'Balance',
          font: {
            size: 14,
            weight: 'bold'
          }
        },
        grid: {
          color: '#e0e0e0',
        },
        ticks: {
          callback: function(value) {
            return '$' + value.toLocaleString();
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
  };

  return (
    <IncomePlanWrapper handleSubmit={handleSubmit}>
      <Box style={styles.mainBody}>
        <div style={styles.errTxt}>{err?.toString()}</div>
        {client ? (
          <div style={{ margin: '10px' }}>
            {client.income_plan_forms ? (
              <>
                <Button
                  endIcon={<RemoveIcon/>}
                  variant='link'
                  disabled={savingView}
                  sx={{
                    color: colors.white,
                    backgroundColor: colors.primaryColor,
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    '&:hover': {
                      color: colors.white,
                      backgroundColor: colors.primaryAccent,
                    }
                  }}
                  onClick={async () => {
                    setSavingView(true);
                    const updated = await toggleForm(auth, client, 'income_plan_forms', false);
                    setSavingView(false);
                    const redesignedClient = await generateRedesignedIncomePlan(updated);
                    setClient(redesignedClient);
                  }}
                >
                  Hide Forms
                </Button>
                <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ 
                      backgroundColor: colors.white,
                      borderRadius: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                    }}>
                      <Box sx={{ 
                        backgroundColor: '#4F5F4B', 
                        borderTopLeftRadius: '8px',
                        borderTopRightRadius: '8px',
                        color: colors.white,
                        textAlign: 'center', 
                        p: 2,
                        fontWeight: 'bold',
                        fontSize: { xs: '1.1rem', md: '1.3rem' },
                        padding: '5px',
                      }}>
                        Client Assumptions
                      </Box>
                      <Box sx={{ p: 2, flexGrow: 1, borderTop: 'none', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
                        <Grid container spacing={1} id="client-edit-form">
                          <Grid item xs={6}>
                            <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>Inflation:</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <MuiTextField
                              type="number"
                              size="small"
                              fullWidth
                              value={client.inflation_rate || ''}
                              name="inflation_rate"
                              onChange={handleTextChange}
                              inputProps={{
                                style: { color: colors.black }
                              }}
                              sx={{ mb: 1 }}
                            />
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>Equities Growth Rate:</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <MuiTextField
                              type="number"
                              size="small"
                              fullWidth
                              disabled
                              value={LOCKED_EQUITY_GROWTH_RATE}
                              inputProps={{
                                style: { color: colors.black }
                              }}
                              sx={{ mb: 1 }}
                            />
                          </Grid>

                          <Grid item xs={6}>
                            <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>Bond Growth Rate:</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <MuiTextField
                              type="number"
                              size="small"
                              fullWidth
                              disabled
                              value={LOCKED_BOND_GROWTH_RATE}
                              inputProps={{
                                style: { color: colors.black }
                              }}
                              sx={{ mb: 1 }}
                            />
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>Effective Tax Rate:</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <MuiTextField
                              type="number"
                              size="small"
                              fullWidth
                              value={client.effective_tax_rate || ''}
                              name="effective_tax_rate"
                              onChange={handleTextChange}
                              inputProps={{
                                style: { color: colors.black }
                              }}
                              sx={{ mb: 1 }}
                            />
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>Asset Withdrawal Rate:</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <MuiTextField
                              type="number"
                              size="small"
                              fullWidth
                              value={client.asset_withdrawal_rate || ''}
                              name="asset_withdrawal_rate"
                              onChange={handleTextChange}
                              inputProps={{
                                style: { color: colors.black }
                              }}
                              sx={{ mb: 1 }}
                            />
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>SoR Risk Average Return:</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <MuiTextField
                              type="number"
                              size="small"
                              fullWidth
                              disabled
                              value={LOCKED_SOR_AVG_RETURN}
                              inputProps={{
                                style: { color: colors.black }
                              }}
                              sx={{ mb: 1 }}
                            />
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>Years to Retire:</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography sx={{ mb: 1 }}>
                              {age && client.retirement_age 
                                ? (client.retirement_age - age < 0 
                                   ? 'Retired' 
                                   : client.retirement_age - age)
                                : 'N/A'
                              }
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>Desired After Tax Income:</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography sx={{ mb: 1 }}>${formatCurrency(Number(client.desired_ati) || 0)}</Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </>
            ) : (
              <Button
                endIcon={<AddIcon/>}
                variant='link'
                disabled={savingView}
                sx={{
                  mb: 2,
                  color: colors.white,
                  backgroundColor: colors.primaryColor,
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  '&:hover': {
                    color: colors.white,
                    backgroundColor: colors.primaryAccent,
                  }
                }}
                onClick={async () => {
                  setSavingView(true);
                  const updated = await toggleForm(auth, client, 'income_plan_forms', true);
                  setSavingView(false);
                  const redesignedClient = await generateRedesignedIncomePlan(updated);
                  setClient(redesignedClient);
                }}
              >
                Show Forms
              </Button>
            )}
              <Grid container spacing={1} sx={{ mt: 1, mb: 2 }}>
                {/* Projected Asset Growth - Detailed Breakdown */}
                <Grid item xs={12} md={6} id="projected-asset-growth">
                  <Box sx={{ 
                    backgroundColor: colors.white,
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    p: 2,
                    height: '100%'
                  }}>
                    <Box sx={{ 
                      backgroundColor: '#4F5F4B', 
                      borderTopLeftRadius: '8px',
                      borderTopRightRadius: '8px',
                      color: colors.white,
                      textAlign: 'center', 
                      p: 2,
                      fontWeight: 'bold',
                      fontSize: { xs: '1.1rem', md: '1.3rem' },
                      mb: 2,
                      mx: -2,
                      mt: -2
                    }}>
                      Projected Asset Growth
                    </Box>
                    
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography sx={{ fontWeight: 'bold' }}>Current Investable Assets:</Typography>
                      </Grid>
                      <Grid item xs={3} sx={{ textAlign: 'right' }}>
                        <Typography>${formatCurrency(currentInvestableAssets)}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <MuiSelect
                          size="small"
                          fullWidth
                          name="include_nq_assets"
                          value={client.include_nq_assets !== undefined ? client.include_nq_assets : true}
                          onChange={handleTextChange}
                          displayEmpty
                        >
                          <MenuItem value={true}>Include NQ Assets</MenuItem>
                          <MenuItem value={false}>Exclude NQ Assets</MenuItem>
                        </MuiSelect>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography sx={{ fontWeight: 'bold' }}>Retirement Year:</Typography>
                      </Grid>
                      <Grid item xs={3} sx={{ textAlign: 'right' }}>
                        <Typography>
                          {client.dob && client.retirement_age
                            ? new Date(client.dob).getFullYear() + client.retirement_age
                            : 'N/A'
                          }
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        {client.dob && client.retirement_age && 
                         ((new Date(client.dob).getFullYear() + client.retirement_age) - new Date().getFullYear()) <= 10 && (
                          <Box sx={{ 
                            backgroundColor: '#dc3545', 
                            color: 'white', 
                            textAlign: 'center', 
                            px: 1, 
                            py: 0.5, 
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            Risk Mgmt Needed
                          </Box>
                        )}
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography sx={{ fontWeight: 'bold' }}>Age(s) in Year of Retirement:</Typography>
                      </Grid>
                      <Grid item xs={3} sx={{ textAlign: 'right' }}>
                        <Typography>
                          {age && client.retirement_age 
                            ? `${client.retirement_age}${spouseAge ? ` / ${spouseAge + (client.retirement_age - age)}` : ''}`
                            : 'N/A'
                          }
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                      </Grid>
                      
                      <Grid item xs={12} sx={{ mt: 2 }}></Grid>
                      
                      <Grid item xs={6}>
                        <Typography sx={{ fontWeight: 'bold' }}>Stock Allocation:</Typography>
                      </Grid>
                      <Grid item xs={3} sx={{ textAlign: 'right' }}>
                        <Typography>${formatCurrency(currentInvestableAssets * ((Number(client.stock_allocation) || 60) / 100))}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <MuiTextField
                          type="number"
                          size="small"
                          fullWidth
                          value={client.stock_allocation || ''}
                          name="stock_allocation"
                          onChange={handleTextChange}
                          inputProps={{
                            style: { color: colors.black, textAlign: 'center' },
                            min: 0,
                            max: 100
                          }}
                          InputProps={{
                            endAdornment: '%'
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography sx={{ fontWeight: 'bold' }}>Bond Allocation:</Typography>
                      </Grid>
                      <Grid item xs={3} sx={{ textAlign: 'right' }}>
                        <Typography>${formatCurrency(currentInvestableAssets * ((Number(client.bond_allocation) || 40) / 100))}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <MuiTextField
                          type="number"
                          size="small"
                          fullWidth
                          value={client.bond_allocation || ''}
                          name="bond_allocation"
                          onChange={handleTextChange}
                          inputProps={{
                            style: { color: colors.black, textAlign: 'center' },
                            min: 0,
                            max: 100
                          }}
                          InputProps={{
                            endAdornment: '%'
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sx={{ mt: 2 }}></Grid>
                      
                      <Grid item xs={6}>
                        <Typography sx={{ fontWeight: 'bold', color: colors.primaryColor }}>Projected Stock Assets:</Typography>
                      </Grid>
                      <Grid item xs={3} sx={{ textAlign: 'right' }}>
                        <Typography sx={{ color: colors.primaryColor, fontWeight: 'bold' }}>
                          ${formatCurrency(currentInvestableAssets * ((Number(client.stock_allocation) || 60) / 100) * Math.pow(1 + (LOCKED_EQUITY_GROWTH_RATE / 100), client.retirement_age && age ? Math.max(0, Number(client.retirement_age) - Number(age)) : 2))}
                        </Typography>
                      </Grid>
                      <Grid item xs={3} sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontSize: 14, color: colors.primaryTextAccent, fontStyle: 'italic' }}>
                          {client.retirement_age && age ? Math.max(0, Number(client.retirement_age) - Number(age)) : 2} Years @ {LOCKED_EQUITY_GROWTH_RATE}%
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography sx={{ fontWeight: 'bold', color: colors.primaryColor }}>Projected Bond Assets:</Typography>
                      </Grid>
                      <Grid item xs={3} sx={{ textAlign: 'right' }}>
                        <Typography sx={{ color: colors.primaryColor, fontWeight: 'bold' }}>
                          ${formatCurrency(currentInvestableAssets * ((Number(client.bond_allocation) || 40) / 100) * Math.pow(1 + (LOCKED_BOND_GROWTH_RATE / 100), client.retirement_age && age ? Math.max(0, Number(client.retirement_age) - Number(age)) : 2))}
                        </Typography>
                      </Grid>
                      <Grid item xs={3} sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontSize: 14, color: colors.primaryTextAccent, fontStyle: 'italic' }}>
                          {client.retirement_age && age ? Math.max(0, Number(client.retirement_age) - Number(age)) : 2} Years @ {LOCKED_BOND_GROWTH_RATE}%
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} sx={{ mt: 2 }}>
                        <Box sx={{ borderTop: '2px solid #4F5F4B', pt: 1 }}>
                          <Grid container>
                            <Grid item xs={6}>
                              <Typography sx={{ fontWeight: 'bold', color: colors.primaryColor }}>Projected Retirement Assets:</Typography>
                            </Grid>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography sx={{ fontWeight: 'bold', color: colors.primaryColor }}>
                                ${formatCurrency(
                                  (currentInvestableAssets * ((Number(client.stock_allocation) || 60) / 100) * Math.pow(1 + (LOCKED_EQUITY_GROWTH_RATE / 100), client.retirement_age && age ? Math.max(0, Number(client.retirement_age) - Number(age)) : 2)) +
                                  (currentInvestableAssets * ((Number(client.bond_allocation) || 40) / 100) * Math.pow(1 + (LOCKED_BOND_GROWTH_RATE / 100), client.retirement_age && age ? Math.max(0, Number(client.retirement_age) - Number(age)) : 2))
                                )}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                
                {/* Projected Income - Detailed Breakdown */}
                <Grid item xs={12} md={6} id="projected-income">
                  <Box sx={{ 
                    backgroundColor: colors.white,
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    p: 2,
                    height: '100%'
                  }}>
                    <Box sx={{ 
                      backgroundColor: '#4F5F4B', 
                      borderTopLeftRadius: '8px',
                      borderTopRightRadius: '8px',
                      color: colors.white,
                      textAlign: 'center', 
                      p: 2,
                      fontWeight: 'bold',
                      fontSize: { xs: '1.1rem', md: '1.3rem' },
                      mb: 2,
                      mx: -2,
                      mt: -2
                    }}>
                      Projected Income
                    </Box>
                    
                    <Grid container spacing={1}>
                      <Grid item xs={8}>
                        <Typography sx={{ fontWeight: 'bold' }}>
                          Projected Annual Target Income (ATI) Needed ({client.dob && client.retirement_age
                            ? new Date(client.dob).getFullYear() + client.retirement_age
                            : 'N/A'
                          }):
                        </Typography>
                      </Grid>
                      <Grid item xs={4} sx={{ textAlign: 'right' }}>
                        <Typography>
                          ${formatCurrency(
                            Number(client.desired_ati) 
                              ? Number(client.desired_ati) * Math.pow(1 + ((Number(client.inflation_rate) || 2.5) / 100), client.retirement_age && age ? Math.max(0, Number(client.retirement_age) - Number(age)) : 2)
                              : 0
                          )}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} sx={{ mt: 2, mb: 1 }}>
                        <Typography sx={{ fontWeight: 'bold', fontSize: 16 }}>Income Sources:</Typography>
                      </Grid>
                      
                      <Grid item xs={8}>
                        <Typography>SSI:</Typography>
                      </Grid>
                      <Grid item xs={4} sx={{ textAlign: 'right' }}>
                        <Typography>
                          ${formatCurrency(ssiIncome)}
                        </Typography>
                      </Grid>

                      <Grid item xs={8}>
                        <Typography>Pension:</Typography>
                      </Grid>
                      <Grid item xs={4} sx={{ textAlign: 'right' }}>
                        <Typography>
                          ${formatCurrency(pensionIncome)}
                        </Typography>
                      </Grid>

                      <Grid item xs={8}>
                        <Typography>Rental:</Typography>
                      </Grid>
                      <Grid item xs={4} sx={{ textAlign: 'right' }}>
                        <Typography>
                          ${formatCurrency(rentalIncome)}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={8}>
                        <Typography>Asset Withdrawals ({client.asset_withdrawal_rate || 4}%):</Typography>
                      </Grid>
                      <Grid item xs={4} sx={{ textAlign: 'right' }}>
                        <Typography>
                          ${formatCurrency(assetWithdrawalIncome)}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} sx={{ mt: 2 }}>
                        <Box sx={{ borderTop: '1px solid #ccc', pt: 1 }}>
                          <Grid container spacing={1}>
                            <Grid item xs={8}>
                              <Typography sx={{ fontWeight: 'bold' }}>Total Projected Income:</Typography>
                            </Grid>
                            <Grid item xs={4} sx={{ textAlign: 'right' }}>
                              <Typography sx={{ fontWeight: 'bold' }}>
                                ${formatCurrency(
                                  ssiIncome + pensionIncome + rentalIncome + assetWithdrawalIncome
                                )}
                              </Typography>
                            </Grid>
                            
                            <Grid item xs={8}>
                              <Typography sx={{ fontWeight: 'bold' }}>Total Projected Annual Target Income (ATI):</Typography>
                            </Grid>
                            <Grid item xs={4} sx={{ textAlign: 'right' }}>
                              <Typography sx={{ fontWeight: 'bold' }}>
                                ${formatCurrency(
                                  (ssiIncome + pensionIncome + rentalIncome + assetWithdrawalIncome) * (1 - ((Number(client.effective_tax_rate) || 0) / 100))
                                )}
                              </Typography>
                            </Grid>
                            
                            <Grid item xs={8}>
                              <Typography sx={{ fontWeight: 'bold', color: colors.primaryColor }}>Projected Income Gap:</Typography>
                            </Grid>
                            <Grid item xs={4} sx={{ textAlign: 'right' }}>
                              <Typography sx={{ fontWeight: 'bold', color: colors.primaryColor }}>
                                ${formatCurrency(
                                    ((ssiIncome + pensionIncome + rentalIncome + assetWithdrawalIncome) * (1 - ((Number(client.effective_tax_rate) || 0) / 100))) -
                                    (Number(client.desired_ati)
                                      ? Number(client.desired_ati) * inflationFactor
                                      : 0)
                                )}
                              </Typography>
                            </Grid>
                            
                            <Grid item xs={8}>
                              <Typography sx={{ fontWeight: 'bold' }}>% Assets Used for Income:</Typography>
                            </Grid>
                            <Grid item xs={4} sx={{ textAlign: 'right' }}>
                              <Typography sx={{ fontWeight: 'bold' }}>100%</Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ 
                p: 3,
                textAlign: 'center',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                maxWidth: '700px',
                margin: '0 auto',
                backgroundColor: colors.white,
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}>
                <Box sx={{ width: '600px', height: '300px', margin: '0 auto' }}>
                  <Line data={chartData} options={options} width={600} height={300} />
                </Box>
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                  <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Sequence of Returns Risk Illustration:</Typography>
                  <Typography sx={{ fontSize: 14, mb: 0.5 }}>- Same starting balance</Typography>
                  <Typography sx={{ fontSize: 14, mb: 0.5 }}>- Same withdrawal amounts</Typography>
                  <Typography sx={{ fontSize: 14, mb: 0.5 }}>- Same average annual returns ({LOCKED_SOR_AVG_RETURN}%)</Typography>
                  <Typography sx={{ fontSize: 14, mb: 0.5 }}>- Scenario 1: Repeating returns of 25%, 10%, 5%, -15%</Typography>
                  <Typography sx={{ fontSize: 14, mb: 0.5 }}>- Scenario 2: Repeating returns of -15%, 5%, 10%, 25%</Typography>
                  <Typography sx={{ fontSize: 14, mb: 0.5 }}>- Negative returns early in retirement can have a massive impact on the chance of outliving savings.</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 'bold' }}>- Are you comfortable leaving your retirement strategy up to chance?</Typography>
                </Box>
              </Box>
          </div>
        ) : <CircularProgress />}
      </Box>
      <Snackbar
        open={open}
        autoHideDuration={2000}
        onClose={() => setOpen(false)}
        message={message}
      />
    </IncomePlanWrapper>
  );
};


export default DefaultRetirementPlan;
